import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowUpRight, MailCheck } from "lucide-react";

/**
 * /olvide-mi-contrasena
 *
 * Solicita el envío del enlace de recuperación. El endpoint responde SIEMPRE con el
 * mismo mensaje genérico (haya o no cuenta), para evitar la enumeración de usuarios.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");  // idle | loading | sent
  const [error, setError] = useState("");

  // Referrer-Policy en la ruta pública (defensa en profundidad)
  useEffect(() => {
    let meta = document.querySelector('meta[name="referrer"]');
    const created = !meta;
    if (created) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "referrer");
      document.head.appendChild(meta);
    }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", "no-referrer");
    return () => {
      if (prev != null) meta.setAttribute("content", prev);
      else if (created) meta.remove();
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setError("");
    const normalized = (email || "").trim().toLowerCase();
    if (!normalized) { setError("Ingresa tu correo electrónico."); return; }
    // validación básica de formato (el backend re-valida)
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(normalized)) {
      setError("El formato del correo no parece válido.");
      return;
    }
    setStatus("loading");
    try {
      await api.post("/auth/forgot-password", { email: normalized });
      setStatus("sent");
    } catch (err) {
      const code = err.response?.status;
      if (code === 429) {
        setError("Has realizado demasiadas solicitudes. Intenta nuevamente más tarde.");
      } else {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
      setStatus("idle");
    }
  };

  return (
    <div className="container-app py-16 grid lg:grid-cols-2 gap-16 items-center">
      <div className="hidden lg:block">
        <div className="label-eyebrow">Recuperar acceso</div>
        <h1 className="serif text-5xl mt-3 leading-tight">Recupera tu contraseña</h1>
        <p className="mt-6 text-muted-foreground max-w-md">
          Te enviaremos un enlace por correo para crear una nueva contraseña.
          El enlace estará disponible durante <strong className="text-foreground">30 minutos</strong> y sólo podrá utilizarse una vez.
        </p>
      </div>
      <form onSubmit={submit} className="panel p-10 max-w-md w-full mx-auto" data-testid="forgot-form" noValidate>
        {status !== "sent" ? (
          <>
            <h2 className="serif text-3xl">Recupera tu contraseña</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Ingresa el correo asociado a tu cuenta y te enviaremos un enlace para crear una nueva contraseña.
            </p>
            <div className="mt-8 space-y-4">
              <div>
                <label htmlFor="forgot-email" className="label-eyebrow">Correo electrónico</label>
                <input
                  id="forgot-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="field mt-1.5"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email"
                  disabled={status === "loading"}
                />
              </div>
              {error && (
                <div className="text-sm text-[hsl(var(--destructive))]" data-testid="forgot-error" role="alert">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={status === "loading"}
                className="btn-primary w-full disabled:opacity-60"
                data-testid="forgot-submit"
              >
                {status === "loading"
                  ? "Enviando…"
                  : (<>Enviar enlace de recuperación <ArrowUpRight size={16} className="ml-2" /></>)}
              </button>
              <div className="text-sm text-muted-foreground text-center">
                <Link to="/login" className="link-underline text-foreground" data-testid="forgot-back-to-login">
                  Volver a iniciar sesión
                </Link>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-4" data-testid="forgot-sent">
            <MailCheck size={48} className="mx-auto text-[hsl(var(--accent))]" />
            <h2 className="serif text-3xl mt-4">Revisa tu correo</h2>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Si el correo está registrado en Colega Link, recibirás un enlace para restablecer tu contraseña.
              Revisa también la carpeta de correo no deseado.
            </p>
            <Link to="/login" className="btn-ghost mt-6 w-full inline-flex justify-center">
              Volver a iniciar sesión
            </Link>
          </div>
        )}
      </form>
    </div>
  );
}
